import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { Pill } from '../../components/audit/AuditVocabulary.js';
import { ApiError } from '../../lib/api.js';
import { fetchReference } from '../../lib/lesson-api.js';
import { createAudit, fetchAudits, fetchPlans, type Audit } from '../../lib/audit-api.js';
import { localName, t } from '../../lib/i18n.js';

/**
 * The engagements themselves — the way into the audit workspace.
 *
 * Not one of spec §11's named screens, and built anyway: the ten it names all
 * act on a particular audit, and without this there is no way to reach one. The
 * alternative would be a workspace that can only be opened from a link somebody
 * already has.
 */
export function AuditList() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [projectId, setProjectId] = useState('');
  const [planId, setPlanId] = useState('');

  const audits = useQuery({ queryKey: ['audits'], queryFn: () => fetchAudits() });
  const plans = useQuery({ queryKey: ['audit-plans'], queryFn: () => fetchPlans() });
  const reference = useQuery({
    queryKey: ['reference', 'audit'],
    queryFn: () => fetchReference('audit'),
  });

  const create = useMutation({
    mutationFn: () =>
      createAudit({ title, projectId, planId: planId || null }),
    onSuccess: () => {
      setError(null);
      setTitle('');
      void queryClient.invalidateQueries({ queryKey: ['audits'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : t("That audit could not be created.")),
  });

  const rows: Audit[] = audits.data?.audits ?? [];
  const projects = reference.data?.projects ?? [];

  return (
    <div className="min-h-full">
      <AppTopBar app="audit" />
      <Breadcrumbs app="audit" trail={['Audits']} />

      <main className="mx-auto max-w-5xl px-4 pb-12 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("Audits")}</h1>
        <p className="mt-1 text-sm text-content-secondary">
          {t("Only the audits your scope reaches are listed.")}
        </p>

        {error && (
          <p role="alert" className="mt-4 text-sm" style={{ color: 'var(--tone-rejected)' }}>
            {error}
          </p>
        )}

        <form
          className="mt-6 rounded-md border border-border bg-surface p-5"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <h2 className="m-0 text-base font-semibold">{t("Start an audit")}</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <label className="flex flex-col text-sm sm:col-span-3">
              <span className="mb-1 text-content-secondary">{t("Title")}</span>
              <input
                className="rounded border border-border px-2 py-1"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </label>
            <label className="flex flex-col text-sm">
              <span className="mb-1 text-content-secondary">{t("Project")}</span>
              <select
                className="rounded border border-border px-2 py-1"
                value={projectId}
                onChange={(event) => setProjectId(event.target.value)}
              >
                <option value="">{t("Choose…")}</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.code} — {localName(project)}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col text-sm">
              <span className="mb-1 text-content-secondary">{t("Plan")}</span>
              <select
                className="rounded border border-border px-2 py-1"
                value={planId}
                onChange={(event) => setPlanId(event.target.value)}
              >
                <option value="">{t("No plan")}</option>
                {(plans.data?.plans ?? []).map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.title}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex items-end">
              <button
                type="submit"
                className="rounded bg-ag-purple px-4 py-2 text-sm font-medium text-white"
                disabled={create.isPending || !title.trim() || !projectId}
              >
                {t("Create")}
              </button>
            </div>
          </div>
        </form>

        {audits.isLoading && <p className="mt-6 text-content-secondary">{t("Loading…")}</p>}

        {!audits.isLoading && rows.length === 0 && (
          <p className="mt-6 rounded-md border border-border bg-surface p-6 text-content-secondary">
            {t("No audits yet.")}
          </p>
        )}

        <ul className="mt-6 list-none space-y-3 p-0">
          {rows.map((audit) => (
            <li
              key={audit.id}
              className="rounded-md border border-border bg-surface p-4 shadow-card"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="m-0 text-base font-semibold">
                  <Link
                    to={`/audit/audits/${audit.id}`}
                    className="text-ag-purple no-underline hover:underline"
                  >
                    {audit.title}
                  </Link>
                </h2>
                <Pill tone={audit.closedAt ? 'positive' : 'neutral'}>
                  {audit.phase.replace('_', '-')}
                </Pill>
              </div>
              <p className="mt-1 mb-0 text-sm text-content-secondary">
                {audit.referenceNo} · {audit.status.replace('_', ' ')}
              </p>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}
